import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import rehypeKatex from 'rehype-katex';
import { detectChartElements, exportChart, ChartData } from '../utils/chartDetection';
import { UnifiedPdfCitationViewer } from './UnifiedPdfCitationViewer';
import { markdownTableComponents as markdownComponents } from './TableExportWrapper';
import SlashCommandAutocomplete from './SlashCommandAutocomplete';
import { useSlashCommands } from '../hooks/useSlashCommands';
import { PromptTemplate } from '../services/promptTemplateService';
import { copyFormattedContent } from '../utils/copyFormatted';
// Legacy citation service removed - using quote-based citations instead

// Simple citation interface to replace ParsedCitation
export interface ParsedCitation {
  id?: string;
  quote?: string;
  text?: string;
  documentId: string;
  docId?: string;
  page?: number;
  startOffset?: number;
  endOffset?: number;
  documentName?: string;
  context?: string;
}

// Citation interfaces for compatibility with legacy offset-based citations
export interface CitationClick {
  docId: string;
  page: number;
  start: number;
  end: number;
  index: number;
}

// Updated citation interfaces for quote-based system
export interface QuoteCitation {
  id: string;
  type: 'quote';
  text: string;
  displayText: string;
  fullMatch: string;
  startIndex: number;
  endIndex: number;
  documentId?: string;
  documentName?: string;
  pageNumber?: number;
  confidence?: number;
}

interface CitationSearchResult {
  found: boolean;
  pageNumber?: number;
  confidence?: number;
  context?: string;
  documentName?: string;
  startOffset?: number;
  endOffset?: number;
  foundText?: string;  // The actual text found by backend (may differ from original)
  originalIndex?: number; // Position in backend's extracted text
}

export interface ChatMessage {
  id: string;
  content: string;
  sender: 'user' | 'ai';
  timestamp: Date;
  isStreaming?: boolean;
  citations?: any[];
  citationType?: 'QUOTE_BASED' | 'OFFSET_BASED';
  systemStatus?: string;
}

interface DocumentWithAlias {
  id: string;
  name: string;
  alias: string;
}

interface ChatInterfaceProps {
  // When a template is used, `displayMessage` carries the template title (shown
  // in chat history); `message` carries the actual prompt body sent to the AI.
  onSendMessage: (message: string, displayMessage?: string) => void;
  messages: ChatMessage[];
  isLoading?: boolean;
  onCitationClick?: (c: any) => void;
  className?: string;
  documents?: Array<{id: string, name: string}>;
  reports?: Array<{id: string, title: string}>;
  citationMap?: any[];
  onClearChat?: () => void;
}

// Enhanced chart export button component
const ChartExportButton: React.FC<{ chartData: ChartData; className?: string }> = ({ chartData, className = '' }) => {
  const [isExporting, setIsExporting] = useState(false);
  
  const handleExport = async () => {
    setIsExporting(true);
    try {
      await exportChart(chartData, {
        filename: `${chartData.title?.replace(/[^a-z0-9]/gi, '_') || 'chart_data'}.xlsx`,
        includeData: true,
        includeImage: !!chartData.element
      });
    } catch (error) {
      console.error('Export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };
  
  return (
    <button
      onClick={handleExport}
      disabled={isExporting}
      className={`chart-export-button flex items-center gap-1 ${className}`}
      title="Export to Excel"
    >
      {isExporting ? (
        <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin"></div>
      ) : (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
          <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
        </svg>
      )}
      Excel
    </button>
  );
};

// New quote citation parsing function
function parseQuoteCitations(text: string): QuoteCitation[] {
  const citations: QuoteCitation[] = [];
  const regex = /\[CITE:\s*"([^"]+)"\]/g;
  let match;
  let index = 0;
  
  while ((match = regex.exec(text)) !== null) {
    const quote = match[1];
    citations.push({
      id: `cite-${index++}`,
      type: 'quote',
      text: quote,
      displayText: quote.length > 50 ? quote.substring(0, 47) + '...' : quote,
      fullMatch: match[0],
      startIndex: match.index,
      endIndex: match.index + match[0].length
    });
  }
  
  return citations;
}

// Helper function for recursive citation extraction
function extractCitationsFromObject(obj: any, path: string, citations: QuoteCitation[], index: { value: number }): void {
  if (typeof obj === 'object' && obj !== null) {
    // Check if this object has the citation format
    if (obj.text && obj.citations && Array.isArray(obj.citations)) {
      obj.citations.forEach((citation: any) => {
        if (citation.quote) {
          const quote = citation.quote;
          citations.push({
            id: `json-cite-${index.value++}`,
            type: 'quote',
            text: quote,
            displayText: quote.length > 50 ? quote.substring(0, 47) + '...' : quote,
            fullMatch: quote,
            startIndex: 0, // JSON citations don't have text positions
            endIndex: 0
          });
        }
      });
    }
    
    // Recursively check all properties
    for (const [key, value] of Object.entries(obj)) {
      const newPath = path ? `${path}.${key}` : key;
      if (typeof value === 'object') {
        extractCitationsFromObject(value, newPath, citations, index);
      } else if (Array.isArray(value)) {
        value.forEach((item, i) => {
          if (typeof item === 'object') {
            extractCitationsFromObject(item, `${newPath}[${i}]`, citations, index);
          }
        });
      }
    }
  }
}

// JSON citation parsing function for new format
function parseJsonCitations(content: string): QuoteCitation[] {
  const citations: QuoteCitation[] = [];
  const index = { value: 0 };
  
  try {
    // Check if content is JSON
    if (!content.trim().startsWith('{')) {
      return citations;
    }
    
    const jsonResponse = JSON.parse(content);
    extractCitationsFromObject(jsonResponse, '', citations, index);
    console.log(`📝 Extracted ${citations.length} citations from JSON response`);
    
  } catch (error) {
    console.log('📝 Content is not valid JSON, skipping JSON citation parsing');
  }
  
  return citations;
}

// Legacy citation parsing (for backwards compatibility)
function parseLegacyCitations(text: string, documents?: Array<{id: string, name: string}>): any[] {
  const citations: any[] = [];
  
  // Standard offset-based citations [CITATION:doc_id,page,start,end]
  const citationRegex = /\[CITATION:\s*(?:doc_id[:=])?\s*([^,]+?)\s*,\s*(?:page\s*,\s*)?(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,.*)?\]/gi;
  let match;
  let count = 1;
  
  while ((match = citationRegex.exec(text)) !== null) {
    const [, docIdOrName, page, start, end] = match;
    
    let actualDocId = docIdOrName.trim();
    if (documents) {
      const foundDoc = documents.find(
        doc => doc.id === actualDocId || doc.name === actualDocId
      );
      if (foundDoc) {
        actualDocId = foundDoc.id;
      }
    }
    
    citations.push({
      match,
      citationData: {
        docId: actualDocId,
        page: Number(page),
        start: Number(start),
        end: Number(end),
        index: count
      },
      type: 'standard'
    });
    count++;
  }
  
  return citations;
}

// Enhanced content renderer with quote citation support
function renderContentWithCitations(
  text: string,
  onCitationClick?: (c: any) => void,
  documents?: Array<{id: string, name: string}>,
  citationType?: string,
  searchState?: {loading: boolean; error: string | null; lastSearchedQuote: string | null}
): React.ReactNode {
  const parts: React.ReactNode[] = [];

  // Check if this is a quote-based, JSON, or legacy message
  const hasQuoteCitations = text.includes('[CITE:');
  const hasOffsetCitations = text.includes('[CITATION:');
  const isJsonResponse = text.trim().startsWith('{');
  
  console.log(`📊 Citation Analysis: Quote=${hasQuoteCitations}, Offset=${hasOffsetCitations}, JSON=${isJsonResponse}, Type=${citationType}`);
  
  if (isJsonResponse) {
    // Use JSON citation parsing for structured responses
    const jsonCitations = parseJsonCitations(text);
    
    if (jsonCitations.length === 0) {
      // No citations found, render as JSON or plain text
      return (
        <div>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeHighlight, rehypeKatex]}
            components={markdownComponents}
          >
            {text}
          </ReactMarkdown>
        </div>
      );
    }
    
    // Render JSON with extracted citations
    return (
      <div className="space-y-4">
        <div>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeHighlight, rehypeKatex]}
            components={markdownComponents}
          >
            {text}
          </ReactMarkdown>
        </div>
        
        {/* Display extracted citations */}
        <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
            📝 Found {jsonCitations.length} Citation{jsonCitations.length !== 1 ? 's' : ''}:
          </div>
          <div className="space-y-2">
            {jsonCitations.map((citation, idx) => (
              <button
                key={citation.id}
                className={`block w-full text-left text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline p-2 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors ${
                  searchState?.loading && searchState?.lastSearchedQuote === citation.text 
                    ? 'opacity-75 cursor-wait' 
                    : ''
                }`}
                onClick={() => onCitationClick && onCitationClick(citation)}
                title={`Click to view: "${citation.text}"`}
                disabled={searchState?.loading && searchState?.lastSearchedQuote === citation.text}
              >
                <div className="flex items-start gap-2">
                  <span className="text-xs bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200 px-1 rounded shrink-0">
                    [{idx + 1}]
                  </span>
                  <span className="text-sm break-words">
                    "{citation.text}"
                  </span>
                  {searchState?.loading && searchState?.lastSearchedQuote === citation.text && (
                    <div className="w-3 h-3 border border-blue-500 border-t-transparent rounded-full animate-spin shrink-0 ml-auto"></div>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  } else if (hasQuoteCitations) {
    // Use new quote-based parsing
    const quoteCitations = parseQuoteCitations(text);
    
    if (quoteCitations.length === 0) {
      return (
        <div>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeHighlight, rehypeKatex]}
            components={markdownComponents}
          >
            {text}
          </ReactMarkdown>
        </div>
      );
    }
    
    let lastIndex = 0;
    quoteCitations.forEach((citation, idx) => {
      // Add text before citation
      if (citation.startIndex > lastIndex) {
        const contentPart = text.substring(lastIndex, citation.startIndex);
        parts.push(
          <div key={`text-${lastIndex}-${citation.startIndex}`}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeHighlight, rehypeKatex]}
              components={markdownComponents}
            >
              {contentPart}
            </ReactMarkdown>
          </div>
        );
      }
      
      // Add clickable citation with loading indicator
      parts.push(
        <button
          key={citation.id}
          className={`text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline mx-1 px-2 py-1 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors relative ${
            searchState?.loading && searchState?.lastSearchedQuote === citation.text 
              ? 'opacity-75 cursor-wait' 
              : ''
          }`}
          onClick={() => onCitationClick && onCitationClick(citation)}
          title={`Click to view: "${citation.text}"`}
          disabled={searchState?.loading && searchState?.lastSearchedQuote === citation.text}
        >
          {searchState?.loading && searchState?.lastSearchedQuote === citation.text && (
            <div className="absolute -top-1 -right-1 w-3 h-3 border border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          )}
          [{citation.displayText}]
        </button>
      );
      
      lastIndex = citation.endIndex;
    });
    
    // Add remaining text
    if (lastIndex < text.length) {
      const remainingContent = text.substring(lastIndex);
      parts.push(
        <div key="text-final">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeHighlight, rehypeKatex]}
            components={markdownComponents}
          >
            {remainingContent}
          </ReactMarkdown>
        </div>
      );
    }
    
    return <div className="space-y-4">{parts}</div>;
  } else {
    // Fallback to legacy parsing for old messages
    const legacyCitations = parseLegacyCitations(text, documents);
    
    if (legacyCitations.length === 0) {
      return (
        <div>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeHighlight, rehypeKatex]}
            components={markdownComponents}
          >
            {text}
          </ReactMarkdown>
        </div>
      );
    }
    
    // Legacy citation rendering (existing logic)
    let lastIndex = 0;
    let count = 1;
    
    legacyCitations.sort((a, b) => a.match.index! - b.match.index!);
    
    for (const { match, citationData, type } of legacyCitations) {
      const idx = match.index!;
      const full = match[0];
      
      if (idx > lastIndex) {
        const contentPart = text.slice(lastIndex, idx);
        parts.push(
          <div key={`text-${lastIndex}-${idx}`}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              rehypePlugins={[rehypeHighlight, rehypeKatex]}
              components={markdownComponents}
            >
              {contentPart}
            </ReactMarkdown>
          </div>
        );
      }
      
      if (citationData && onCitationClick) {
        parts.push(
          <button
            key={`citation-${count}`}
            className="text-primary underline hover:text-blue-600 transition-colors mx-1 px-2 py-1 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/30"
            onClick={() => onCitationClick(citationData)}
          >
            [{count}]
          </button>
        );
      }
      
      lastIndex = idx + full.length;
      count++;
    }
    
    if (lastIndex < text.length) {
      const remainingContent = text.slice(lastIndex);
      parts.push(
        <div key={`text-${lastIndex}-end`}>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeHighlight, rehypeKatex]}
            components={markdownComponents}
          >
            {remainingContent}
          </ReactMarkdown>
        </div>
      );
    }
    
    return <div className="space-y-4">{parts}</div>;
  }
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({
  onSendMessage,
  messages,
  isLoading = false,
  className = '',
  onCitationClick,
  documents,
  reports,
  citationMap,
  onClearChat
}) => {
  const [inputMessage, setInputMessage] = useState('');
  const [detectedCharts, setDetectedCharts] = useState<ChartData[]>([]);
  const [isOverlayMode, setIsOverlayMode] = useState(false);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [selectedCitation, setSelectedCitation] = useState<ParsedCitation | null>(null);
  const [currentDocumentId, setCurrentDocumentId] = useState<string>('');
  const [citationSearchState, setCitationSearchState] = useState<{
    loading: boolean;
    error: string | null;
    lastSearchedQuote: string | null;
  }>({ loading: false, error: null, lastSearchedQuote: null });
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  // Store the selected template's prompt directly
  const [selectedTemplatePrompt, setSelectedTemplatePrompt] = useState<string | null>(null);

  // Track which message was just copied for visual feedback
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);

  // Initialize slash commands
  const {
    isAutocompleteVisible,
    searchQuery,
    autocompletePosition,
    handleInputChange,
    handleTemplateSelect,
    closeAutocomplete,
    handleKeyDown: handleSlashKeyDown,
    setInputRef,
    getCurrentPromptForMessage
  } = useSlashCommands({
    onTemplateSelect: (template: PromptTemplate, displayText: string, actualPrompt: string) => {
      console.log('Template selected:', template.title);
      console.log('Display text:', displayText);
      console.log('Actual prompt:', actualPrompt.substring(0, 100) + '...');
      
      // Store the actual prompt to be sent
      setSelectedTemplatePrompt(actualPrompt);
      
      // Force a small delay to ensure state is updated before potential send
      setTimeout(() => {
        console.log('✅ Template prompt stored in state');
      }, 10);
    }
  });
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();

  // Assign aliases to documents (doc1, doc2, etc.)
  const documentsWithAliases: DocumentWithAlias[] = React.useMemo(() => {
    if (!documents) return [];
    return documents.map((doc, index) => ({
      ...doc,
      alias: `doc${index + 1}`
    }));
  }, [documents]);

  // Set input ref for slash commands
  useEffect(() => {
    if (inputRef.current) {
      setInputRef(inputRef.current);
    }
  }, [setInputRef]);
  
  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current && !isOverlayMode) {
      messagesEndRef.current.scrollIntoView({ 
        behavior: 'smooth',
        block: 'nearest'
      });
    }
  }, [messages, isOverlayMode]);

  // Detect charts when messages change
  useEffect(() => {
    if (messagesContainerRef.current) {
      const charts = detectChartElements(messagesContainerRef.current);
      setDetectedCharts(charts);
    }
  }, [messages]);

  // Set current document ID from documents
  useEffect(() => {
    if (documents && documents.length > 0) {
      setCurrentDocumentId(documents[0].id);
    }
  }, [documents]);

  // Enhanced citation click handler for quote-based citations
  const handleCitationClick = async (citation: QuoteCitation | any) => {
    try {
      // Handle quote-based citations
      if (citation.type === 'quote') {
        console.log('🔍 Searching for quote:', citation.text);
        
        // Clear previous errors and set loading state
        setCitationSearchState({
          loading: true,
          error: null,
          lastSearchedQuote: citation.text
        });
        
        const authToken = localStorage.getItem('riskninja-token');
        if (!authToken) {
          setCitationSearchState({
            loading: false,
            error: 'Authentication required. Please log in to view citations.',
            lastSearchedQuote: citation.text
          });
          console.error('❌ No auth token available');
          return;
        }
        
        // Document verification logic - determine which document to search
        let searchDocumentId = currentDocumentId;
        
        // Try to find documents referenced in recent AI messages
        const documentsUsedInChat = messages
          .filter(m => m.sender === 'ai')
          .slice(-5) // Check last 5 AI messages
          .flatMap(m => {
            // Extract document references like "doc1", "doc2" from the message
            const docRefs = m.content.match(/doc\d+/g) || [];
            return docRefs.map(ref => {
              const index = parseInt(ref.replace('doc', '')) - 1;
              return documents?.[index]?.id;
            }).filter(Boolean);
          });
        
        // Use the most recently referenced document, or fall back to selected
        if (documentsUsedInChat.length > 0) {
          const contextDocId = documentsUsedInChat[documentsUsedInChat.length - 1];
          if (contextDocId) {
            searchDocumentId = contextDocId;
            console.log(`📄 Using document from chat context: ${searchDocumentId}`);
          }
        }
        
        if (!searchDocumentId) {
          setCitationSearchState({
            loading: false,
            error: 'No document selected. Please ensure a document is loaded.',
            lastSearchedQuote: citation.text
          });
          console.error('❌ No document ID available');
          return;
        }
        
        // Add comprehensive diagnostic logging
        console.log('🔍 Citation Search Debug:', {
          originalQuote: citation.text,
          quoteTruncated: citation.text.substring(0, 100) + (citation.text.length > 100 ? '...' : ''),
          searchDocumentId,
          documentsAvailable: documents?.map(d => ({ id: d.id, name: d.name })),
          documentsFromContext: documentsUsedInChat.length,
          currentDocumentFallback: currentDocumentId
        });

        try {
          const apiUrl = process.env.REACT_APP_API_URL;
          const response = await fetch(`${apiUrl}/api/chat/citations/find-quote`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authToken}`
            },
            body: JSON.stringify({
              documentId: searchDocumentId,  // Use the determined document ID
              quote: citation.text
            })
          });
          
          if (!response.ok) {
            throw new Error(`Server error: ${response.status} ${response.statusText}`);
          }
          
          const result: CitationSearchResult = await response.json();
          
          if (result.found && result.pageNumber) {
            console.log(`✅ Found quote on page ${result.pageNumber} (confidence: ${result.confidence || 'N/A'})`);
            
            // Log the text comparison for debugging
            if (result.foundText && result.foundText !== citation.text) {
              console.log(`📝 Text normalization occurred:`);
              console.log(`   Original: "${citation.text}"`);
              console.log(`   Found:    "${result.foundText}"`);
              console.log(`   Length diff: ${citation.text.length - result.foundText.length}`);
            }
            
            // Clear loading state on success
            setCitationSearchState({
              loading: false,
              error: null,
              lastSearchedQuote: null
            });
            
            // Create a parsed citation for the PDF viewer - USE FOUND TEXT, not original
            const textForPdfSearch = result.foundText || citation.text;
            const parsedCitation: ParsedCitation = {
              id: citation.id,
              page: result.pageNumber,
              text: textForPdfSearch,  // Use the text that was actually found in backend
              // Use returned offsets if available, otherwise calculate from found text length
              startOffset: result.startOffset || 0,
              endOffset: result.endOffset || textForPdfSearch.length,
              docId: searchDocumentId,
              documentId: searchDocumentId,
              documentName: result.documentName || 'Document'
            };
            
            // Set state for PDF viewer
            setSelectedCitation(parsedCitation);
            setShowPdfViewer(true);
            
            // Log success with details
            console.log(`📄 Opening PDF viewer:`, {
              page: result.pageNumber,
              document: result.documentName,
              confidence: result.confidence,
              hasContext: !!result.context
            });
            
          } else {
            setCitationSearchState({
              loading: false,
              error: `Quote not found in document "${result.documentName || 'Unknown'}". The text may have been paraphrased or the document may have changed.`,
              lastSearchedQuote: citation.text
            });
            console.log('❌ Quote not found in document');
          }
        } catch (error) {
          setCitationSearchState({
            loading: false,
            error: `Failed to search for citation: ${error instanceof Error ? error.message : 'Unknown error'}`,
            lastSearchedQuote: citation.text
          });
          console.error('❌ Citation search failed:', error);
        }
        
        return;
      }
      
      // Handle legacy offset-based citations
      if (onCitationClick) {
        onCitationClick(citation);
      }
      
    } catch (error) {
      setCitationSearchState({
        loading: false,
        error: `Unexpected error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        lastSearchedQuote: citation.text || 'Unknown quote'
      });
      console.error('❌ Error handling citation click:', error);
    }
  };

  // Handle overlay mode
  const toggleOverlayMode = () => {
    setIsOverlayMode(!isOverlayMode);
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setIsOverlayMode(false);
    }
  };

  // Calculate dynamic height based on content
  const calculateChatHeight = () => {
    if (isOverlayMode) return 'h-[85vh]';
    
    const messageCount = messages.length;
    if (messageCount === 0) return 'h-[500px]';
    if (messageCount <= 3) return 'h-[600px]';
    if (messageCount <= 6) return 'h-[700px]';
    if (messageCount <= 10) return 'h-[800px]';
    return 'h-[900px]';
  };

  const handleClearChat = () => {
    if (onClearChat && messages.length > 0) {
      if (window.confirm('Are you sure you want to clear this chat? This action cannot be undone.')) {
        onClearChat();
      }
    }
  };

  const handleSendMessage = () => {
    if (inputMessage.trim() && !isLoading) {
      const displayMessage = inputMessage.trim();
      const messageToSend = getCurrentPromptForMessage(inputMessage);

      // If the resolved prompt differs from the input, a template was selected.
      // Pass both so the parent can render the title in the chat UI and send the
      // full prompt body to the AI without persisting it.
      if (messageToSend !== displayMessage) {
        onSendMessage(messageToSend, displayMessage);
      } else {
        onSendMessage(messageToSend);
      }
      setInputMessage('');
      
      // Clear the selected template prompt after sending
      setSelectedTemplatePrompt(null);
      
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    // Let slash commands handle their own keyboard events first
    handleSlashKeyDown(e, inputMessage);
    
    if (e.key === 'Enter' && !e.shiftKey && !isAutocompleteVisible) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  
  const handleInputChangeInternal = (value: string) => {
    setInputMessage(value);
    handleInputChange(value);
    
    // Clear the selected template prompt if user manually types (unless it's from template selection)
    // The template selection will re-set this value immediately after
    if (selectedTemplatePrompt && !value.includes('/')) {
      setSelectedTemplatePrompt(null);
    }
  };
  
  const handleTemplateSelectInternal = (template: PromptTemplate) => {
    handleTemplateSelect(template, inputMessage, setInputMessage);
  };

  const formatTime = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const handleCopyMessage = async (messageId: string, content: string) => {
    // Find the rendered message element
    const messageElement = document.querySelector(`[data-message-id="${messageId}"]`) as HTMLElement;

    if (messageElement) {
      const success = await copyFormattedContent(content, messageElement);
      if (success) {
        setCopiedMessageId(messageId);
        // Clear the "Copied!" feedback after 2 seconds
        setTimeout(() => setCopiedMessageId(null), 2000);
      }
    } else {
      // Fallback to plain text copy if element not found
      try {
        await navigator.clipboard.writeText(content);
        setCopiedMessageId(messageId);
        setTimeout(() => setCopiedMessageId(null), 2000);
      } catch (err) {
        console.error('Copy failed:', err);
      }
    }
  };

  const chatContent = (
    <div 
      ref={chatContainerRef}
      className={`flex flex-col bg-white dark:bg-dark-surface rounded-xl shadow-lg border border-gray-200 dark:border-dark-border canvas-like-chat ${calculateChatHeight()} ${
        isOverlayMode ? 'w-[90vw] max-w-6xl' : 'w-full'
      }`}
    >
      {/* Header with citation system status */}
      <div className="flex flex-col gap-3 p-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-dark-surface rounded-t-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-500 rounded-full"></div>
            <span className="text-sm font-medium text-secondary dark:text-dark-text">Risk Ninja AI</span>
            
            {/* Citation system status indicator */}
          {messages.some(m => m.citationType === 'QUOTE_BASED') && (
            <span className="text-xs bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-2 py-1 rounded-full">
              ✅ Quote Citations
            </span>
          )}
          
          {messages.length > 0 && (
            <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full">
              {messages.length} message{messages.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-1">
          {onClearChat && messages.length > 0 && (
            <button
              onClick={handleClearChat}
              className="p-2 text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              title="Clear chat"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6h14zM10 11v6M14 11v6"/>
              </svg>
            </button>
          )}
          
          <button
            onClick={toggleOverlayMode}
            className="p-2 text-gray-500 dark:text-gray-400 hover:text-primary dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            title={isOverlayMode ? "Minimize chat" : "Expand chat"}
          >
            {isOverlayMode ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>
              </svg>
            )}
          </button>
        </div>
      </div>
      
      {/* Document and Report Context Display */}
      {((documents && documents.length > 0) || (reports && reports.length > 0)) && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Context:</span>
          <div className="flex flex-wrap gap-1">
            {/* Documents */}
            {documents && documents.map((doc, index) => (
              <span 
                key={doc.id} 
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs rounded-md"
                title={doc.name}
              >
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                </svg>
                <span className="truncate max-w-[150px]">
                  {doc.name.length > 20 ? `${doc.name.substring(0, 20)}...` : doc.name}
                </span>
                <span className="text-[10px] text-blue-600 dark:text-blue-400">
                  (doc{index + 1})
                </span>
              </span>
            ))}
            
            {/* Reports */}
            {reports && reports.map((report, index) => (
              <span 
                key={report.id} 
                className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs rounded-md"
                title={report.title}
              >
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" clipRule="evenodd" />
                  <path fillRule="evenodd" d="M4 5a2 2 0 012-2 1 1 0 000 2H4v10h12V5h-2a1 1 0 100-2 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5z" clipRule="evenodd" />
                </svg>
                <span className="truncate max-w-[150px]">
                  {report.title.length > 20 ? `${report.title.substring(0, 20)}...` : report.title}
                </span>
                <span className="text-[10px] text-purple-600 dark:text-purple-400">
                  (report{index + 1})
                </span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>

      {/* Messages Area */}
      <div 
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto p-8 space-y-8 bg-gradient-to-br from-slate-50/80 via-white to-slate-50/60 dark:from-slate-900/80 dark:via-slate-800 dark:to-slate-900/60 backdrop-blur-sm chat-messages-container"
      >
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center py-16">
            <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-3xl flex items-center justify-center mb-6 shadow-lg">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" className="text-primary">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M13 8H7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <path d="M17 12H7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            <h3 className="text-2xl font-semibold text-secondary dark:text-dark-text mb-3">
              Welcome to Risk Ninja AI
            </h3>
            <p className="text-accent dark:text-dark-muted text-lg max-w-2xl leading-relaxed">
              {user 
                ? 'I\'m here to help you analyze policies, assess risks, and optimize your insurance coverage. Upload documents or ask me anything about commercial insurance.'
                : 'Sign in to unlock powerful AI-driven insurance analysis and risk assessment tools.'
              }
            </p>
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'} animate-in fade-in duration-500`}
              >
                <div
                  className={`max-w-4xl rounded-2xl px-6 py-4 shadow-sm ${
                    message.sender === 'user'
                      ? 'chat-message-user'
                      : 'chat-message-ai border border-gray-200/50 dark:border-gray-700/50'
                  }`}
                >
                  {message.sender === 'ai' && (
                    <div className="flex items-center gap-3 mb-3 pb-2 border-b border-gray-200/30 dark:border-gray-700/30">
                      <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center shadow-md">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                          <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                        </svg>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-primary dark:text-blue-400">Risk Ninja AI</span>
                          {message.citationType === 'QUOTE_BASED' && (
                            <span className="text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 px-2 py-0.5 rounded-full">
                              Quote Citations
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {formatTime(message.timestamp)}
                        </div>
                      </div>
                    </div>
                  )}
                  <div className={`text-base leading-relaxed ${message.sender === 'user' ? 'text-white' : 'text-gray-800 dark:text-gray-100'}`}>
                    <div
                      data-message-id={message.id}
                      className="overflow-x-auto markdown-content prose prose-lg max-w-none dark:prose-invert"
                    >
                      {renderContentWithCitations(
                        message.content,
                        handleCitationClick,
                        documents,
                        message.citationType,
                        citationSearchState
                      )}
                    </div>
                    {message.isStreaming && (
                      <span className="inline-block w-1 h-6 bg-current ml-2 animate-pulse rounded-full"></span>
                    )}
                  </div>
                  {message.sender === 'user' && (
                    <div className="flex items-center justify-end mt-2 text-xs text-blue-100">
                      <span>{formatTime(message.timestamp)}</span>
                    </div>
                  )}
                  {message.sender === 'ai' && !message.isStreaming && (
                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-200/30 dark:border-gray-700/30">
                      <div className="flex items-center gap-2">
                        {detectedCharts.length > 0 && (
                          <div className="text-xs text-blue-600 dark:text-blue-400 flex items-center gap-1">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M3 3v18h18v-2H5V3H3zm4 14h2V9H7v8zm4 0h2v-6h-2v6zm4 0h2V7h-2v10z"/>
                            </svg>
                            {detectedCharts.length} chart{detectedCharts.length !== 1 ? 's' : ''} detected
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => handleCopyMessage(message.id, message.content)}
                        className={`flex items-center gap-1 px-3 py-1 text-xs rounded-lg transition-all ${
                          copiedMessageId === message.id
                            ? 'text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30'
                            : 'text-gray-500 dark:text-gray-400 hover:text-primary dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                        }`}
                        title="Copy response with formatting"
                      >
                        {copiedMessageId === message.id ? (
                          <>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                            Copied!
                          </>
                        ) : (
                          <>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                            </svg>
                            Copy
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input Area */}
      <div className="p-6 bg-white/70 dark:bg-gray-900/70 backdrop-blur-sm border-t border-gray-200/50 dark:border-gray-700/50 rounded-b-xl">
        {user ? (
          <>
            <div className="flex items-end gap-4 max-w-6xl mx-auto">
              <div className="flex-1">
                <div className="relative">
                  <input
                    ref={inputRef}
                    type="text"
                    value={inputMessage}
                    onChange={(e) => {
                      setInputMessage(e.target.value);
                      handleInputChange(e.target.value);
                    }}
                    onKeyDown={(e) => handleSlashKeyDown(e, inputMessage)}
                    onKeyPress={handleKeyPress}
                    placeholder="Ask about policies, risks, coverage gaps, or premium optimization..."
                    disabled={isLoading}
                    className="w-full px-6 py-4 bg-white dark:bg-gray-800 border border-gray-300/50 dark:border-gray-600/50 rounded-2xl text-gray-800 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/50 transition-all duration-200 disabled:opacity-50 shadow-sm text-base"
                  />
                  
                  {/* Slash Command Autocomplete - attached to input */}
                  <SlashCommandAutocomplete
                    isVisible={isAutocompleteVisible}
                    searchQuery={searchQuery}
                    onSelect={(template) => handleTemplateSelect(template, inputMessage, setInputMessage)}
                    onClose={closeAutocomplete}
                    position={autocompletePosition}
                  />
                  {isLoading && (
                    <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                      <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={handleSendMessage}
                disabled={!inputMessage.trim() || isLoading}
                className="px-8 py-4 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 disabled:from-gray-300 disabled:to-gray-400 dark:disabled:from-gray-600 dark:disabled:to-gray-700 text-white rounded-2xl font-medium transition-all duration-200 disabled:cursor-not-allowed flex items-center gap-2 shadow-lg hover:shadow-xl transform hover:scale-105 disabled:transform-none"
              >
                {isLoading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Analyzing...</span>
                  </>
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <line x1="22" y1="2" x2="11" y2="13"></line>
                      <polygon points="22,2 15,22 11,13 2,9"></polygon>
                    </svg>
                    <span>Send</span>
                  </>
                )}
              </button>
            </div>
            
            {/* Quick Actions */}
            <div className="flex flex-wrap gap-3 mt-6 max-w-6xl mx-auto">
              <button
                onClick={() => setInputMessage("Compare coverage limits across my policies")}
                className="px-4 py-2 text-sm bg-white/80 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:border-blue-300 hover:text-blue-600 dark:hover:text-blue-400 transition-all duration-200 backdrop-blur-sm shadow-sm hover:shadow-md"
              >
                💼 Compare Coverage
              </button>
              <button
                onClick={() => setInputMessage("Identify potential risk exposures in my business")}
                className="px-4 py-2 text-sm bg-white/80 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:border-blue-300 hover:text-blue-600 dark:hover:text-blue-400 transition-all duration-200 backdrop-blur-sm shadow-sm hover:shadow-md"
              >
                ⚠️ Risk Assessment
              </button>
              <button
                onClick={() => setInputMessage("Find opportunities to reduce insurance costs")}
                className="px-4 py-2 text-sm bg-white/80 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:border-blue-300 hover:text-blue-600 dark:hover:text-blue-400 transition-all duration-200 backdrop-blur-sm shadow-sm hover:shadow-md"
              >
                💰 Cost Optimization
              </button>
              <button
                onClick={() => setInputMessage("Analyze my premium trends and projections")}
                className="px-4 py-2 text-sm bg-white/80 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:border-blue-300 hover:text-blue-600 dark:hover:text-blue-400 transition-all duration-200 backdrop-blur-sm shadow-sm hover:shadow-md"
              >
                📊 Premium Analysis
              </button>
            </div>
            
            {/* Slash command hint */}
            <div className="mt-3 text-xs text-gray-500 dark:text-gray-400 text-center">
              Type "/" to use prompt templates • ⌘K for template management
            </div>
          </>
        ) : (
          <div className="text-center py-8 max-w-md mx-auto">
            <div className="mb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <svg className="w-8 h-8 text-gray-400 dark:text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
              </div>
              <h4 className="text-lg font-semibold text-secondary dark:text-dark-text mb-2">
                Sign in to get started
              </h4>
              <p className="text-sm text-accent dark:text-dark-muted leading-relaxed">
                Unlock AI-powered insurance analysis, risk assessment, and policy optimization tools.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  if (isOverlayMode) {
    return (
      <div 
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
        onClick={handleOverlayClick}
      >
        {chatContent}
      </div>
    );
  }

  return (
    <div className={className}>
      {chatContent}
      
      {/* Citation Error/Success Toast */}
      {citationSearchState.error && (
        <div className="fixed top-4 right-4 z-40 max-w-md">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 shadow-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                  Citation Search Failed
                </h3>
                <div className="mt-1 text-sm text-red-700 dark:text-red-300">
                  {citationSearchState.error}
                </div>
                {citationSearchState.lastSearchedQuote && (
                  <div className="mt-2 text-xs text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-900/30 p-2 rounded italic">
                    Searched quote: "{citationSearchState.lastSearchedQuote.substring(0, 100)}..."
                  </div>
                )}
                <div className="mt-3 flex space-x-2">
                  <button
                    onClick={() => setCitationSearchState(prev => ({ ...prev, error: null }))}
                    className="text-xs bg-red-100 dark:bg-red-800 text-red-800 dark:text-red-200 px-2 py-1 rounded hover:bg-red-200 dark:hover:bg-red-700 transition-colors"
                  >
                    Dismiss
                  </button>
                  {currentDocumentId && (
                    <button
                      onClick={() => {
                        // Try opening the document directly without search
                        setCitationSearchState(prev => ({ ...prev, error: null }));
                        // Could implement manual document browsing here
                      }}
                      className="text-xs bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 px-2 py-1 rounded hover:bg-blue-200 dark:hover:bg-blue-700 transition-colors"
                    >
                      Browse Document
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* PDF Citation Viewer for quote-based citations */}
      {showPdfViewer && selectedCitation && (
        <UnifiedPdfCitationViewer
          documentId={selectedCitation.documentId}
          citation={{
            documentId: selectedCitation.documentId,
            documentName: selectedCitation.documentName || 'Unknown Document',
            page: selectedCitation.page || 1,
            startOffset: selectedCitation.startOffset || 0,
            endOffset: selectedCitation.endOffset || 0,
            text: selectedCitation.text || '',
            context: `Citation from ${selectedCitation.documentName || 'Unknown Document'}, page ${selectedCitation.page || 1}`
          }}
          onClose={() => {
            setShowPdfViewer(false);
            setSelectedCitation(null);
            // Clear any lingering errors when closing the viewer
            setCitationSearchState(prev => ({ ...prev, error: null }));
          }}
          useAnnotationViewer={true} // Always use annotation viewer for quote citations
        />
      )}
    </div>
  );
};

export default ChatInterface;